import React, { FC, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { track } from '@adsk/acs-web-analytics';
import {
  ENTITLEMENTS_PLATFORM_TYPES,
  EntitlementsProject,
} from '@adsk/alloy-provider';
import {
  actions as ActionTypes,
  OnAction,
  SkeletonLoadingVertebra,
  Table,
  TABLE_SIZES,
  TableBodyRow,
  TablePaginationFooter,
  TableSkeletonLoadingRow,
} from '@adsk/alloy-react-table';
import { TableBodyRowProps } from '@adsk/alloy-react-table/es/TableBodyRow';
import {
  AnalyticsEvents,
  DEFAULT_LIMIT,
  TABLE_SORT_DIRECTION,
} from '../../constants';
import i18n from '../../helpers/i18n';
import { calculatePageFromOffset, getProjectUrl } from '../../utils';
import { useAccessLevelContext } from '../Contexts/AccessLevelContext';
import HttpErrorState from '../HttpErrorState';
import { scrollToTop } from '../ScrollContainer';
import {
  AccountNameCell,
  CreatedAtCell,
  NameCell,
  NumberCell,
  TypeCell,
} from './Columns';

const LinkWithoutColor = styled.a({
  textDecoration: 'none',
  color: 'inherit',
});

export type PageState = {
  sortDirection: TABLE_SORT_DIRECTION;
  sortKey: keyof EntitlementsProject;
  offset: number;
  sortChangeHandler: (
    sortKey: keyof EntitlementsProject,
    sortDirection: TABLE_SORT_DIRECTION
  ) => void;
  pageChangeHandler: (offset: number) => void;
};

export type ProjectsTableProps = {
  loading: boolean;
  error?: HttpError;
  data: EntitlementsProject[];
  recordCount: number;
  pageState: PageState;
  isTemplate?: boolean;
};

export const ProjectsAndTemplatesTable = ({
  loading = false,
  error = undefined,
  data,
  recordCount,
  pageState,
  isTemplate = false,
}: ProjectsTableProps) => {
  const previousOffset = useRef<number>(0);
  const { accountsUserIsAccAccountAdmin, accountsError, accountsLoading } =
    useAccessLevelContext();

  const {
    sortDirection,
    sortKey,
    offset,
    sortChangeHandler,
    pageChangeHandler,
  } = pageState;

  const handleSortChange = useCallback(
    (sortKey: keyof EntitlementsProject, isDescending: boolean) => {
      const direction = isDescending
        ? TABLE_SORT_DIRECTION.DESCENDING
        : TABLE_SORT_DIRECTION.ASCENDING;
      const tableSortAnalytic = isTemplate
        ? AnalyticsEvents.templatesTableSortColumn
        : AnalyticsEvents.projectsTableSortColumn;

      track(tableSortAnalytic, {
        column_name: sortKey,
        sorting_order: direction,
      });

      sortChangeHandler(sortKey, direction);
    },
    [isTemplate, sortChangeHandler]
  );

  const handleOffsetChange = useCallback(
    (offset: number, previousPage: number) => {
      const paginationChangeAnalytic = isTemplate
        ? AnalyticsEvents.templatesPaginationChange
        : AnalyticsEvents.projectsPaginationChange;

      track(paginationChangeAnalytic, {
        nextPage: calculatePageFromOffset(offset),
        previousPage,
        isFirstPage: offset === 0,
        isLastPage: offset + DEFAULT_LIMIT >= recordCount,
      });
      pageChangeHandler(offset);
    },
    [pageChangeHandler, recordCount, isTemplate]
  );

  const handleRowSelectionChange = useCallback(
    (rowNumber: number) => {
      const selectedProject = data[rowNumber];
      const platform =
        selectedProject.platform === ENTITLEMENTS_PLATFORM_TYPES.ACC
          ? ENTITLEMENTS_PLATFORM_TYPES.ACC
          : ENTITLEMENTS_PLATFORM_TYPES.BIM360;
      const navigateToProjectAnalytic = isTemplate
        ? AnalyticsEvents.templatesNavigateToProject
        : AnalyticsEvents.projectsNavigateToProject;

      track(navigateToProjectAnalytic, {
        platform,
        is_account_admin_on_project: !!accountsUserIsAccAccountAdmin.find(
          (account) => selectedProject.accountId === account.id
        ),
      });
    },
    [data, accountsUserIsAccAccountAdmin, isTemplate]
  );

  const columns = useMemo(() => {
    return [
      {
        accessor: 'platform',
        renderHeader: () => i18n.t('TYPE'),
        renderCell: TypeCell,
        width: 100,
        maxWidth: 100,
      },
      {
        accessor: 'name',
        renderHeader: () => i18n.t('NAME'),
        renderCell: NameCell,
      },
      ...(isTemplate
        ? []
        : [
            {
              accessor: 'jobNumber',
              renderHeader: () => i18n.t('NUMBER'),
              renderCell: NumberCell,
              maxWidth: 500,
            },
          ]),
      {
        accessor: 'accountName',
        renderHeader: () => i18n.t('ACCOUNT_NAME'),
        renderCell: AccountNameCell,
        maxWidth: 500,
      },
      {
        accessor: 'createdAt',
        renderHeader: () => i18n.t('CREATED_ON'),
        renderCell: CreatedAtCell,
        width: 100,
      },
    ];
  }, [isTemplate]);

  const onAction: OnAction<EntitlementsProject> = ({ action }) => {
    switch (action.type) {
      case ActionTypes.gotoPage:
        const newOffset = action.pageIndex * DEFAULT_LIMIT; //i.e 0,25,50
        const previousPage = calculatePageFromOffset(previousOffset.current); // i.e 1,2,3
        scrollToTop();
        handleOffsetChange(newOffset, previousPage);
        previousOffset.current = newOffset;
        break;
      case ActionTypes.toggleRowSelected:
        handleRowSelectionChange(action.id);
        break;
      case ActionTypes.toggleSortBy:
        const { columnId, desc } = action;
        handleSortChange(columnId, !!desc);
        break;
      default:
        break;
    }
  };

  const renderBodyRow: FC<TableBodyRowProps<EntitlementsProject>> = ({
    row,
    ...otherProps
  }) => (
    <LinkWithoutColor key={row.original.id} href={getProjectUrl(row.original)}>
      <TableBodyRow row={row} {...otherProps} />
    </LinkWithoutColor>
  );

  const renderFooter = useCallback(
    ({ table }) => {
      if (recordCount > 0) {
        return <TablePaginationFooter {...table} />;
      }
      return null;
    },
    [recordCount]
  );

  if ((!loading && error) || (accountsError && !accountsLoading)) {
    return <HttpErrorState errorMessage={i18n.t('COULD_NOT_LOAD_PROJECTS')} />;
  }

  return (
    <Table
      columns={columns}
      data={data}
      defaultSize={TABLE_SIZES.LARGE}
      onAction={onAction}
      isResizable
      // pagination
      isPaginatable
      manualPagination
      pageSize={DEFAULT_LIMIT}
      pageIndex={Math.floor(offset / DEFAULT_LIMIT)}
      totalDataCount={recordCount}
      // end pagination
      isSortable
      isSingleSelectable
      setTableOptions={(options) => ({
        ...options,
        disableMultiSort: true,
        disableSortRemove: true,
        manualSortBy: true,
        initialState: {
          ...options.initialState,
          sortBy: [
            {
              id: sortKey,
              desc: sortDirection === TABLE_SORT_DIRECTION.DESCENDING,
            },
          ],
        },
      })}
      renderBodyRow={renderBodyRow}
      renderEmptyState={({ table }) =>
        loading ? (
          <>
            {Array.from({ length: DEFAULT_LIMIT }).map((_, key) => (
              <TableSkeletonLoadingRow
                key={key}
                visibleColumns={table.visibleColumns}
                renderCell={() => <SkeletonLoadingVertebra />}
              />
            ))}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            {i18n.t('NO_RESULTS_FOUND')}
          </div>
        )
      }
      renderFooter={renderFooter}
    />
  );
};
